import React, { useCallback, useMemo } from 'react';
import { Text } from '../typography/Typography/Typography';
import debounce from 'lodash.debounce';

interface ClipboardTextProps {
  text: number | string;
  textToCopy?: string;
  onCopy?: (text: string) => void;
  onError?: (text: string, error: Error) => void;
}

/**
 * Displays text that can be copied to clipboard when clicked.
 * @param text Text to be displayed and copied.
 * @param textToCopy alternative Text to be copied to clipboard. If not provided, the displayed text will be copied.
 * @param onCopy Optional callback invoked after text is successfully copied.
 * @param onError Optional callback invoked when copying fails.
 */
export const ClipboardText: React.FC<ClipboardTextProps> = ({
  text,
  textToCopy,
  onCopy,
  onError,
}) => {
  const copyToClipboard = useCallback(
    (textToCopy: string) => {
      navigator.clipboard
        .writeText(textToCopy)
        .then(() => {
          onCopy?.(textToCopy);
        })
        .catch((error: unknown) => {
          const err = error instanceof Error ? error : new Error(String(error));
          onError?.(textToCopy, err);
        });
    },
    [onCopy, onError],
  );

  const debouncedCopyToClipboard = useMemo(
    () => debounce(() => copyToClipboard(textToCopy ?? text.toString()), 50),
    [copyToClipboard, textToCopy, text],
  );

  return (
    <Text $pointer onClick={debouncedCopyToClipboard}>
      {text}
    </Text>
  );
};
